import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Switch,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { musicService } from '../../../../services/musicInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('MusicInput');

type MusicInputScreenRouteProp = RouteProp<RootStackParamList, 'MusicInput'>;

export const MusicInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<MusicInputScreenRouteProp>();

  const { musicData, isEditing } = route.params || {};

  const [preludeMusic, setPreludeMusic] = useState(
    musicData?.preludeMusic || ''
  );
  const [processionalMusic, setProcessionalMusic] = useState(
    musicData?.processionalMusic || ''
  );
  const [hymnsSongs, setHymnsSongs] = useState(
    musicData?.hymnsSongs || ''
  );
  const [specialMusic, setSpecialMusic] = useState(
    musicData?.specialMusic || ''
  );
  const [recessionalMusic, setRecessionalMusic] = useState(
    musicData?.recessionalMusic || ''
  );
  const [favoriteSongs, setFavoriteSongs] = useState(
    musicData?.favoriteSongs || ''
  );
  const [liveMusicians, setLiveMusicians] = useState(
    musicData?.liveMusicians || ''
  );
  const [recordedMusic, setRecordedMusic] = useState(
    musicData?.recordedMusic || false
  );
  const [avoidMusic, setAvoidMusic] = useState(
    musicData?.avoidMusic || ''
  );
  const [culturalMusic, setCulturalMusic] = useState(
    musicData?.culturalMusic || ''
  );
  const [specialRequests, setSpecialRequests] = useState(
    musicData?.specialRequests || ''
  );
  const [notes, setNotes] = useState(
    musicData?.notes || ''
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!preludeMusic.trim() && !processionalMusic.trim() && !hymnsSongs.trim() && !favoriteSongs.trim()) {
      Alert.alert('Error', 'Please enter at least one music preference');
      return;
    }

    try {
      setSaving(true);

      const musicUpdateData = {
        preludeMusic: preludeMusic.trim(),
        processionalMusic: processionalMusic.trim(),
        hymnsSongs: hymnsSongs.trim(),
        specialMusic: specialMusic.trim(),
        recessionalMusic: recessionalMusic.trim(),
        favoriteSongs: favoriteSongs.trim(),
        liveMusicians: liveMusicians.trim(),
        recordedMusic: recordedMusic,
        avoidMusic: avoidMusic.trim(),
        culturalMusic: culturalMusic.trim(),
        specialRequests: specialRequests.trim(),
        notes: notes.trim(),
      };

      let savedMusic;

      if (isEditing && musicData?.id) {
        // Update existing record
        savedMusic = await musicService.updateMusic(
          musicData.id,
          musicUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedMusic?.data || savedMusic;
        logger.debug('Music preferences updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection of lock/unlock
        navigation.navigate('MusicLockUnlock', { musicId: updatedData.id });
      } else {
        // Create new record
        savedMusic = await musicService.createMusic(musicUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedMusic?.data || savedMusic;
        logger.debug('Music preferences saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('MusicLockUnlock', { musicId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving music preferences:', error);
      Alert.alert('Error', 'Failed to save music preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Music Preferences
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Prelude Music:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={preludeMusic}
              onChangeText={setPreludeMusic}
              placeholder="Music to be played before the service begins (e.g., soft instrumental, hymns)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Processional Music:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={processionalMusic}
              onChangeText={setProcessionalMusic}
              placeholder="Music for the entrance/processional (e.g., traditional hymns, classical pieces)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Hymns & Songs:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={hymnsSongs}
              onChangeText={setHymnsSongs}
              placeholder="Specific hymns or songs to be sung (e.g., Amazing Grace, favorite religious songs)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Music:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={specialMusic}
              onChangeText={setSpecialMusic}
              placeholder="Special musical performances (e.g., solos, duets, choir pieces)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Recessional Music:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={recessionalMusic}
              onChangeText={setRecessionalMusic}
              placeholder="Music for the exit/recessional (e.g., uplifting hymns, celebration of life songs)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Favorite Songs:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={favoriteSongs}
              onChangeText={setFavoriteSongs}
              placeholder="Your favorite songs or meaningful music (e.g., songs that represent your life, bring comfort)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Live Musicians:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={liveMusicians}
              onChangeText={setLiveMusicians}
              placeholder="Preferred musicians or musical arrangements (e.g., organist, pianist, choir, string quartet)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Recorded Music Acceptable:</Text>
            <View style={styles.switchContainer}>
              <Switch
                value={recordedMusic}
                onValueChange={setRecordedMusic}
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={recordedMusic ? '#FFFFFF' : '#FFFFFF'}
              />
              <Text style={styles.switchLabel}>
                {recordedMusic ? 'Yes, recorded music is acceptable' : 'Prefer live music only'}
              </Text>
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Music to Avoid:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={avoidMusic}
              onChangeText={setAvoidMusic}
              placeholder="Any music or songs you'd prefer to avoid (e.g., specific genres, songs with difficult memories)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Cultural Music:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={culturalMusic}
              onChangeText={setCulturalMusic}
              placeholder="Cultural or traditional music preferences (e.g., Irish ballads, gospel, cultural hymns)"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Requests:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={specialRequests}
              onChangeText={setSpecialRequests}
              placeholder="Any other special musical requests or considerations"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Notes:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={notes}
              onChangeText={setNotes}
              placeholder="Any additional notes or instructions regarding music"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>
          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    minHeight: s(80),
    textAlignVertical: 'top',
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  switchLabel: {
    marginLeft: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});

export default MusicInputScreen;